import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { getApiRequest } from "../msalConfigCreditional";

export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired. Redirecting to sign in.");
    this.name = "SessionExpiredError";
  }
}

export function isSessionExpiredError(error) {
  return error instanceof SessionExpiredError || error?.name === "SessionExpiredError";
}

// One in-flight acquisition. A forced refresh replaces a non-forced one so a
// 401 retry is not stuck on the token the server just rejected. Concurrent
// forced refreshes share a single MSAL call.
let inFlight = null;
let inFlightForce = false;

// Set synchronously before navigation so parallel callers cannot start a
// second redirect (or a popup, which Chrome and Edge block on page load).
let redirectStarted = false;
let redirectHandled = null;

const SILENT_TOKEN_TIMEOUT_MS = 12000;
const SILENT_TIMEOUT_MESSAGE = "Silent token acquisition timed out";

// MSAL runs acquireTokenSilent through a hidden iframe. These BrowserAuthError
// codes mean that iframe never came back with a usable response — third-party
// cookies blocked, a slow/unreachable login.microsoftonline.com, corporate
// browser policy, etc. They are not InteractionRequiredAuthError, but they
// mean the same thing in practice: silent refresh cannot succeed, so the user
// must be sent through an interactive sign-in instead of letting the request
// fail outright.
const IFRAME_FAILURE_ERROR_CODES = new Set([
  "monitor_window_timeout",
  "block_iframe_reload",
  "empty_window_error",
  "iframe_closed_prematurely",
]);

function withTimeout(promise, ms, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function needsInteraction(error) {
  return (
    error instanceof InteractionRequiredAuthError ||
    error?.message === SILENT_TIMEOUT_MESSAGE ||
    IFRAME_FAILURE_ERROR_CODES.has(error?.errorCode)
  );
}

function isInteractionInProgress(error) {
  return error?.errorCode === "interaction_in_progress";
}

async function ensureRedirectHandled(msalInstance) {
  if (!redirectHandled) {
    redirectHandled = msalInstance.handleRedirectPromise().catch((error) => {
      redirectHandled = null;
      throw error;
    });
  }
  return redirectHandled;
}

function beginSignInRedirect(msalInstance, request) {
  if (redirectStarted) {
    throw new SessionExpiredError();
  }
  redirectStarted = true;
  // Do not await: the redirect navigates away, and waiting lets the
  // permissions timeout treat sign-in as a failed permissions call.
  msalInstance.acquireTokenRedirect(request).catch((redirectError) => {
    if (!isInteractionInProgress(redirectError)) {
      redirectStarted = false;
      console.error("Sign-in redirect failed:", redirectError);
    }
  });
  throw new SessionExpiredError();
}

async function acquireToken({ forceRefresh = false } = {}) {
  const { msalInstance, initPromise } = await import("../index");
  await initPromise;
  await ensureRedirectHandled(msalInstance);

  if (redirectStarted) {
    throw new SessionExpiredError();
  }

  const accounts = msalInstance.getAllAccounts();
  const account = msalInstance.getActiveAccount() || accounts[0];

  if (!account) {
    throw new Error("No active account found. Please login again.");
  }

  const apiRequest = getApiRequest();
  const silentRequest = {
    ...apiRequest,
    account,
    forceRefresh,
  };
  const silentPromise = msalInstance.acquireTokenSilent(silentRequest);

  try {
    const response = await withTimeout(
      silentPromise,
      SILENT_TOKEN_TIMEOUT_MS,
      SILENT_TIMEOUT_MESSAGE
    );
    return response.accessToken;
  } catch (error) {
    if (redirectStarted || isInteractionInProgress(error)) {
      throw new SessionExpiredError();
    }
    if (!needsInteraction(error)) {
      throw error;
    }

    if (error?.message === SILENT_TIMEOUT_MESSAGE) {
      // The hidden iframe is still running. Let it finish before starting a
      // redirect, which MSAL rejects while that request is open.
      try {
        const late = await silentPromise;
        return late.accessToken;
      } catch (lateError) {
        if (redirectStarted || isInteractionInProgress(lateError)) {
          throw new SessionExpiredError();
        }
        if (!needsInteraction(lateError)) {
          throw lateError;
        }
      }
    }

    beginSignInRedirect(msalInstance, {
      ...apiRequest,
      account,
    });
  }
}

/**
 * Get a valid access token for API calls.
 * Silent refresh covers an expired access token. When Azure requires the
 * user again (refresh token expired after the tab sat overnight), sign-in
 * continues in this tab via redirect. A popup is not used: page-load popups
 * are blocked in Chrome and Edge.
 * @param {object} [options]
 * @param {boolean} [options.forceRefresh] - Skip MSAL's cached access token
 *   and mint a fresh one (used when a request just failed with 401).
 * @returns {Promise<string>} Access token
 */
export function getValidToken(options = {}) {
  const forceRefresh = !!options.forceRefresh;

  if (inFlight && (inFlightForce || !forceRefresh)) {
    return inFlight;
  }

  inFlightForce = forceRefresh;
  const run = acquireToken({ forceRefresh }).finally(() => {
    if (inFlight === run) {
      inFlight = null;
      inFlightForce = false;
    }
  });
  inFlight = run;
  return run;
}
